import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Loader2, Plus, X, Trash2, Home, Sparkles,
  ClipboardList, History, Save, Wind, Package, ShieldAlert, Heart, RefreshCw, MessageCircle, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PropertyDetailMap, { ATTENTION, ATTENTION_ORDER, attentionOf } from "@/components/PropertyDetailMap";
import PropertyScoreCard from "@/components/PropertyScoreCard";

const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
const labelCls = "text-[11px] uppercase tracking-[0.12em] text-muted-c mb-1 block";
const ZONE_TYPES = ["kitchen", "bathroom", "bedroom", "living", "balcony", "windows", "storage", "other"];
const DEFAULT_ZONES = [
  { name: "Kitchen", type: "kitchen" }, { name: "Bathroom", type: "bathroom" },
  { name: "Living Room", type: "living" }, { name: "Bedroom", type: "bedroom" },
  { name: "Balcony", type: "balcony" }, { name: "Windows", type: "windows" },
  { name: "Storage", type: "storage" },
];
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

// ---- Zone editor drawer ----
function ZoneEditor({ zone, onClose, onSave, onDelete }) {
  const [z, setZ] = useState(zone);
  const set = (k, v) => setZ((p) => ({ ...p, [k]: v }));
  const setDp = (i, k, v) => setZ((p) => { const dp = [...(p.detail_points || [])]; dp[i] = { ...dp[i], [k]: v }; return { ...p, detail_points: dp }; });
  const addDp = () => setZ((p) => ({ ...p, detail_points: [...(p.detail_points || []), { label: "", attention: "standard", note: "" }] }));
  const rmDp = (i) => setZ((p) => ({ ...p, detail_points: (p.detail_points || []).filter((_, x) => x !== i) }));
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto bg-[#101011] border-l border-[color:var(--border-subtle)] p-6" onClick={(e) => e.stopPropagation()} data-testid="zone-editor">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg font-semibold text-white">Edit zone</h3>
          <button onClick={onClose} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Zone name</label><input className={inputCls} value={z.name} onChange={(e) => set("name", e.target.value)} data-testid="zone-name" /></div>
            <div><label className={labelCls}>Type</label>
              <select className={inputCls} value={z.type || "other"} onChange={(e) => set("type", e.target.value)}>
                {ZONE_TYPES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Attention level</label>
            <div className="flex flex-wrap gap-2" data-testid="zone-attention">
              {ATTENTION_ORDER.map((k) => (
                <button key={k} onClick={() => set("attention", k)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${z.attention === k ? "bg-white/10" : "hover:bg-white/5"}`}
                  style={{ borderColor: z.attention === k ? ATTENTION[k].dot : "var(--border-subtle)", color: ATTENTION[k].text }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ATTENTION[k].dot }} />{ATTENTION[k].label}
                </button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Scope</label><input className={inputCls} value={z.scope || ""} onChange={(e) => set("scope", e.target.value)} placeholder="e.g. Cabinet interiors + residue removal" /></div>
          <div><label className={labelCls}>Notes</label><textarea rows={2} className={inputCls} value={z.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + " mb-0"}>Detail points — "what Ascendia sees"</label>
              <button onClick={addDp} className="inline-flex items-center gap-1 text-xs text-gold-soft hover:text-gold" data-testid="add-detail-point"><Plus className="h-3.5 w-3.5" /> Add</button>
            </div>
            <div className="space-y-2">
              {(z.detail_points || []).map((dp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className={inputCls + " flex-1"} placeholder="e.g. Hinges, adhesive residue" value={dp.label} onChange={(e) => setDp(i, "label", e.target.value)} />
                  <select className="rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-2 py-2 text-xs text-white" value={dp.attention || "standard"} onChange={(e) => setDp(i, "attention", e.target.value)}>
                    {ATTENTION_ORDER.map((k) => <option key={k} value={k}>{ATTENTION[k].label}</option>)}
                  </select>
                  <button onClick={() => rmDp(i)} className="text-muted-c hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {!(z.detail_points || []).length && <p className="text-xs text-muted-c">No detail points yet — these show the individual points a standard clean misses.</p>}
            </div>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={() => onDelete(z)} className="inline-flex items-center gap-1.5 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-red-300 hover:bg-white/5"><Trash2 className="h-4 w-4" /> Delete</button>
          <button onClick={() => onSave(z)} data-testid="zone-save" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><Save className="h-4 w-4" /> Save zone</button>
        </div>
      </div>
    </div>
  );
}

// ---- Passport detail-fields editor ----
function DetailsEditor({ p, onClose, onSave }) {
  const [f, setF] = useState({
    windows_config: p.windows_config || "", cabinetry: p.cabinetry || "", access_info: p.access_info || "",
    appliances: (p.appliances || []).join(", "), surfaces: (p.surfaces || []).join(", "),
    special_care: (p.special_care || []).join(", "), problem_areas: (p.problem_areas || []).join(", "),
    residue_history: (p.residue_history || []).join(", "),
    fragrance_free: p.preferences?.fragrance_free || false, products_avoid: (p.preferences?.products_avoid || []).join(", "),
    pref_notes: p.preferences?.notes || "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const save = () => onSave({
    windows_config: f.windows_config, cabinetry: f.cabinetry, access_info: f.access_info,
    appliances: toArr(f.appliances), surfaces: toArr(f.surfaces), special_care: toArr(f.special_care),
    problem_areas: toArr(f.problem_areas), residue_history: toArr(f.residue_history),
    preferences: { fragrance_free: f.fragrance_free, products_avoid: toArr(f.products_avoid), notes: f.pref_notes },
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto bg-[#101011] border-l border-[color:var(--border-subtle)] p-6" onClick={(e) => e.stopPropagation()} data-testid="details-editor">
        <div className="flex items-center justify-between mb-5"><h3 className="font-display text-lg font-semibold text-white">Property details</h3><button onClick={onClose} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button></div>
        <div className="space-y-4">
          <div><label className={labelCls}>Windows & glass configuration</label><input className={inputCls} value={f.windows_config} onChange={(e) => set("windows_config", e.target.value)} placeholder="e.g. Floor-to-ceiling, 3 balconies" /></div>
          <div><label className={labelCls}>Cabinetry</label><input className={inputCls} value={f.cabinetry} onChange={(e) => set("cabinetry", e.target.value)} placeholder="e.g. High-gloss lacquer" /></div>
          <div><label className={labelCls}>Surfaces / materials (comma separated)</label><input className={inputCls} value={f.surfaces} onChange={(e) => set("surfaces", e.target.value)} placeholder="Marble, oak, chrome" /></div>
          <div><label className={labelCls}>Appliances (comma separated)</label><input className={inputCls} value={f.appliances} onChange={(e) => set("appliances", e.target.value)} placeholder="Oven, Fridge, Dishwasher" /></div>
          <div><label className={labelCls}>Access information</label><input className={inputCls} value={f.access_info} onChange={(e) => set("access_info", e.target.value)} placeholder="Gate code, elevator, contact person" /></div>
          <div><label className={labelCls}>Special-care areas (comma separated)</label><input className={inputCls} value={f.special_care} onChange={(e) => set("special_care", e.target.value)} placeholder="Marble floors, delicate cabinetry" /></div>
          <div><label className={labelCls}>Known problem areas (comma separated)</label><input className={inputCls} value={f.problem_areas} onChange={(e) => set("problem_areas", e.target.value)} placeholder="Balcony drainage, window tracks" /></div>
          <div className="gold-hairline my-1" />
          <p className="kicker">Ascendia Memory · your preferences</p>
          <label className="flex items-center gap-2 text-sm text-secondary"><input type="checkbox" checked={f.fragrance_free} onChange={(e) => set("fragrance_free", e.target.checked)} /> Prefer fragrance-free products</label>
          <div><label className={labelCls}>Products to avoid (comma separated)</label><input className={inputCls} value={f.products_avoid} onChange={(e) => set("products_avoid", e.target.value)} placeholder="Acidic cleaners on marble" /></div>
          <div><label className={labelCls}>Preference notes</label><textarea rows={2} className={inputCls} value={f.pref_notes} onChange={(e) => set("pref_notes", e.target.value)} /></div>
        </div>
        <button onClick={save} data-testid="details-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-semibold text-black hover:bg-gold-soft"><Save className="h-4 w-4" /> Save details</button>
      </div>
    </div>
  );
}

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">{items.map((x, i) => <span key={i} className="rounded-full border border-[color:var(--border-subtle)] bg-white/[0.03] px-2.5 py-0.5 text-xs text-secondary">{x}</span>)}</div>
);

// ---- One-tap rebook with "what changed" + Property Memory ----
const CHANGE_TAGS = ["Renovation since last visit", "New furniture / appliances", "New residue or damage", "New rooms to include", "Areas to exclude", "Nothing major changed"];
function RebookModal({ p, onClose, onDone }) {
  const [mode, setMode] = useState("repeat");
  const [service, setService] = useState(p.recommendation || "");
  const [tags, setTags] = useState([]);
  const [changes, setChanges] = useState("");
  const [busy, setBusy] = useState(false);
  const memo = p.preferences || {};
  const known = [
    p.sqm && `${p.sqm} sqm`, p.bedrooms && `${p.bedrooms} bed`, p.bathrooms && `${p.bathrooms} bath`,
    p.property_type, ...(p.surfaces || []).slice(0, 3),
    (p.problem_areas || []).length && `known: ${(p.problem_areas || [])[0]}`,
    memo.fragrance_free && "fragrance-free preferred",
  ].filter(Boolean);
  const toggle = (t) => setTags((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/client/properties/${p.id}/rebook`, { mode, service, change_tags: tags, changes });
      toast.success(`Request sent · ${data.ref} — Ascendia will prepare your plan`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };
  const MODES = [["repeat", "Repeat same scope"], ["modify", "Modify scope"], ["different", "Different service"]];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="rebook-modal">
      <div className="w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl bg-[#101011] border border-[color:var(--border-subtle)] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="kicker mb-1">Detail this property again</p>
            <h3 className="font-display text-xl font-semibold text-white">{p.nickname}</h3>
          </div>
          <button onClick={onClose} className="text-muted-c hover:text-white" data-testid="rebook-close"><X className="h-5 w-5" /></button>
        </div>
        {/* Property Memory */}
        {known.length > 0 && (
          <div className="mt-3 rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.02] p-3" data-testid="rebook-memory">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gold-soft flex items-center gap-1.5 mb-1.5"><Heart className="h-3.5 w-3.5" /> We already know</p>
            <Chips items={known} />
            <p className="text-xs text-muted-c mt-2">No need to start over — just tell us what changed.</p>
          </div>
        )}
        <div className="mt-4 space-y-4">
          <div>
            <label className={labelCls}>What would you like?</label>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`rebook-mode-${k}`}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-medium transition-colors ${mode === k ? "border-gold bg-gold/10 text-white" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
          </div>
          {mode === "different" && (
            <div><label className={labelCls}>Which service?</label><input className={inputCls} value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Deep clean, Move-out detail" /></div>
          )}
          <div>
            <label className={labelCls}>What changed since our last visit?</label>
            <div className="flex flex-wrap gap-2">
              {CHANGE_TAGS.map((t) => (
                <button key={t} onClick={() => toggle(t)} data-testid={`rebook-tag-${t.slice(0,6)}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${tags.includes(t) ? "border-gold bg-gold/10 text-white" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>
                  {tags.includes(t) && <Check className="h-3 w-3 text-gold-soft" />}{t}
                </button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Anything else? (optional)</label><textarea rows={2} className={inputCls} value={changes} onChange={(e) => setChanges(e.target.value)} placeholder="Tell Ascendia about anything new" /></div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="rebook-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Send service request
        </button>
      </div>
    </div>
  );
}

export default function PropertyPassport() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [zoneEdit, setZoneEdit] = useState(null); // zone | null
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rebookOpen, setRebookOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/client/properties/${id}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
    api.get(`/client/properties/${id}/timeline`).then(({ data }) => setTimeline(data || [])).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const persistZones = async (zones) => {
    try { const { data } = await api.patch(`/client/properties/${id}/passport`, { zones }); setP(data); }
    catch (e) { toast.error("Could not save zones"); }
  };
  const saveZone = (z) => { const zones = [...(p.zones || [])]; const i = zones.findIndex((x) => x.id === z.id); if (i >= 0) zones[i] = z; else zones.push(z); persistZones(zones); setZoneEdit(null); toast.success("Zone saved"); };
  const deleteZone = (z) => { persistZones((p.zones || []).filter((x) => x.id !== z.id)); setZoneEdit(null); toast.success("Zone removed"); };
  const addZone = () => setZoneEdit({ id: uid(), name: "", type: "other", attention: "standard", scope: "", notes: "", detail_points: [] });
  const seedDefaults = () => { const zones = DEFAULT_ZONES.map((z) => ({ id: uid(), attention: "standard", scope: "", notes: "", detail_points: [], ...z })); persistZones(zones); toast.success("Default zones added"); };
  const saveDetails = async (patch) => { try { const { data } = await api.patch(`/client/properties/${id}/passport`, patch); setP(data); setDetailsOpen(false); toast.success("Details saved"); } catch { toast.error("Could not save"); } };
  const requestService = () => { const q = new URLSearchParams({ property: p.nickname, city: p.city || "", sqm: p.sqm || "", type: p.property_type || "", beds: p.bedrooms || "", baths: p.bathrooms || "" }); nav(`/property-intelligence?${q.toString()}`); };

  if (notFound) return <div className="ascendia-card p-10 text-center"><p className="text-white font-medium">Property not found</p><button onClick={() => nav("/account/properties")} className="mt-4 text-gold-soft hover:text-gold text-sm">← Back to my properties</button></div>;
  if (!p) return <div className="py-24 text-center text-muted-c"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  const specs = [p.bedrooms && `${p.bedrooms} bed`, p.bathrooms && `${p.bathrooms} bath`, p.balcony && `${p.balcony} balcony`, p.sqm && `${p.sqm} sqm`].filter(Boolean).join(" • ");
  const memo = p.preferences || {};
  const hasDetails = p.windows_config || p.cabinetry || (p.appliances || []).length || (p.surfaces || []).length || (p.special_care || []).length || (p.problem_areas || []).length || p.access_info;

  return (
    <div className="space-y-6" data-testid="property-passport">
      <button onClick={() => nav("/account/properties")} className="inline-flex items-center gap-1.5 text-sm text-muted-c hover:text-white" data-testid="passport-back"><ArrowLeft className="h-4 w-4" /> My properties</button>

      {/* Header */}
      <div className="ascendia-card card-accent-top p-6 sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="kicker mb-1 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-gold-soft" /> Ascendia Property Passport</p>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white">{p.nickname}</h1>
            <p className="mt-1 text-sm text-muted-c">{[p.property_type, p.address || p.city].filter(Boolean).join(" · ") || "Details not set"}</p>
            {specs && <p className="mt-2 text-sm text-secondary">{specs}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setRebookOpen(true)} data-testid="passport-rebook" className="inline-flex items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><RefreshCw className="h-4 w-4" /> Detail this again</button>
            <button onClick={requestService} data-testid="passport-request-service" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white hover:bg-white/5">Request a service <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => nav("/ask")} data-testid="passport-ask" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white hover:bg-white/5"><MessageCircle className="h-4 w-4 text-gold-soft" /> Ask Ascendia</button>
          </div>
        </div>
      </div>

      {/* Ascendia assessment: Property Score + recommendation */}
      <PropertyScoreCard score={p.score} />
      {p.recommendation && (
        <div className="ascendia-card p-5 flex items-start gap-3" data-testid="property-recommendation">
          <Sparkles className="h-5 w-5 text-gold-soft shrink-0 mt-0.5" />
          <div>
            <p className="kicker mb-1">Ascendia recommends</p>
            <p className="text-sm text-secondary">{p.recommendation}</p>
          </div>
        </div>
      )}

      {/* Detail Map */}
      <div className="ascendia-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2"><ClipboardList className="h-4.5 w-4.5 text-gold-soft" /> Detail Map</h2>
          <div className="flex gap-2">
            {!(p.zones || []).length && <button onClick={seedDefaults} data-testid="seed-default-zones" className="text-xs rounded-lg border border-[color:var(--border-subtle)] px-3 py-1.5 text-white/80 hover:bg-white/5">Add default zones</button>}
            <button onClick={addZone} data-testid="add-zone" className="inline-flex items-center gap-1.5 text-xs rounded-lg bg-white/5 border border-[color:var(--border-subtle)] px-3 py-1.5 text-white hover:bg-white/10"><Plus className="h-3.5 w-3.5" /> Add zone</button>
          </div>
        </div>
        <p className="text-sm text-muted-c mb-4 max-w-2xl">A visual map of your property. Ascendia marks the attention each zone needs — so you can see exactly where the work goes.</p>
        <PropertyDetailMap zones={p.zones || []} onZoneClick={(z) => setZoneEdit(z)} selectedId={zoneEdit?.id} />
      </div>

      {/* Property details / Memory */}
      <div className="ascendia-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2"><Home className="h-4.5 w-4.5 text-gold-soft" /> Property details</h2>
          <button onClick={() => setDetailsOpen(true)} data-testid="edit-details" className="text-xs rounded-lg border border-[color:var(--border-subtle)] px-3 py-1.5 text-white/80 hover:bg-white/5">Edit details</button>
        </div>
        {!hasDetails && !memo.notes && !(memo.products_avoid || []).length && !memo.fragrance_free ? (
          <p className="text-sm text-muted-c">Add windows, surfaces, cabinetry, access and your care preferences so Ascendia knows your home.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
            {p.windows_config && <div><p className="text-muted-c text-xs flex items-center gap-1.5"><Wind className="h-3.5 w-3.5" /> Windows & glass</p><p className="text-secondary mt-0.5">{p.windows_config}</p></div>}
            {p.cabinetry && <div><p className="text-muted-c text-xs">Cabinetry</p><p className="text-secondary mt-0.5">{p.cabinetry}</p></div>}
            {(p.surfaces || []).length > 0 && <div><p className="text-muted-c text-xs">Surfaces</p><div className="mt-1"><Chips items={p.surfaces} /></div></div>}
            {(p.appliances || []).length > 0 && <div><p className="text-muted-c text-xs flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Appliances</p><div className="mt-1"><Chips items={p.appliances} /></div></div>}
            {p.access_info && <div><p className="text-muted-c text-xs">Access</p><p className="text-secondary mt-0.5">{p.access_info}</p></div>}
            {(p.special_care || []).length > 0 && <div><p className="text-muted-c text-xs flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> Special care</p><div className="mt-1"><Chips items={p.special_care} /></div></div>}
            {(p.problem_areas || []).length > 0 && <div><p className="text-muted-c text-xs">Problem areas</p><div className="mt-1"><Chips items={p.problem_areas} /></div></div>}
            {(memo.fragrance_free || (memo.products_avoid || []).length > 0 || memo.notes) && (
              <div className="sm:col-span-2 rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.02] p-3">
                <p className="text-muted-c text-xs flex items-center gap-1.5"><Heart className="h-3.5 w-3.5 text-gold-soft" /> Ascendia Memory</p>
                <p className="text-secondary mt-1">{[memo.fragrance_free && "Fragrance-free preferred", (memo.products_avoid || []).length && `Avoid: ${memo.products_avoid.join(", ")}`, memo.notes].filter(Boolean).join(" · ")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="ascendia-card p-6">
        <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4"><History className="h-4.5 w-4.5 text-gold-soft" /> Service history</h2>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-c">No services recorded for this property yet.</p>
        ) : (
          <ol className="relative border-l border-[color:var(--border-subtle)] ml-1.5 space-y-5" data-testid="property-timeline">
            {timeline.map((e, i) => (
              <li key={i} className="ml-5">
                <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-gold" />
                <p className="text-sm text-white font-medium">{e.label}{e.status ? ` · ${e.status}` : ""}</p>
                <p className="text-xs text-muted-c">{(e.date || "").slice(0, 10)}{e.ref ? ` · ${e.ref}` : ""}</p>
                {e.report_ready && e.portal_token && (
                  <a href={`/portal/${e.portal_token}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-gold-soft hover:text-gold" data-testid="timeline-report-link">View property report <ArrowRight className="h-3 w-3" /></a>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {zoneEdit && <ZoneEditor zone={zoneEdit} onClose={() => setZoneEdit(null)} onSave={saveZone} onDelete={deleteZone} />}
      {detailsOpen && <DetailsEditor p={p} onClose={() => setDetailsOpen(false)} onSave={saveDetails} />}
      {rebookOpen && <RebookModal p={p} onClose={() => setRebookOpen(false)} onDone={() => { setRebookOpen(false); nav("/account/bookings"); }} />}
    </div>
  );
}
